/**
 * Fixture data for `?fixture=report-v2&state=<state>` -- the owner-approved
 * Weekly report screen v2 (DEC-CHAT-20261010-403): the design's own sample
 * (mockups/cockpit-v3/weekly-report/Main.dc.html renderVals()), as a RAW
 * `GET /api/cockpit/reports` envelope in the exact shape
 * `azure-functions/cis/CisCockpitReports` returns. The artboard gate loads
 * this fixture once per design state (notReady, ready, generating, draft,
 * approved, pdfFailed, published) and compares it to the design's render of
 * that state.
 *
 * "Today" in this world is Saturday 10 Oct 2026 (W41). The view is the custom
 * period 1 Sep - 10 Oct 2026 (40 days, covers W36 - W41), which is the
 * design's own default. Names in the design's mockup are display data only;
 * this file carries no person names -- the signed-in user is `sm@example.com`
 * and shows as "you".
 */

"use strict";

export const V2_STATES = ["notReady", "ready", "generating", "draft", "approved", "pdfFailed", "published"];
export const V2_TODAY = "2026-10-10";
export const V2_ME = "sm@example.com";
export const V2_CURRENT_WEEK = "2026-10-05"; // W41
export const V2_PERIOD = { periodStart: "2026-09-01", periodEnd: "2026-10-10" };

const SITE = "https://negmarine.sharepoint.com/sites/ConstructionSupervision";

function versionUrl(code, reportNumber, versionId) {
  return `${SITE}/_vti_history/${Number(versionId) * 512}/WeeklyReports-${code}/${code}-WR-${reportNumber}.docx`;
}

function pdfUrl(code, reportNumber, name) {
  return `${SITE}/WeeklyReports-${code}/PDF/${encodeURIComponent(name || `${code}_Progress_Report_${reportNumber}`)}.pdf`;
}

/** The Word file name the design shows for SS418 report 9. */
export const V2_PDF_NAME = "SS418 Progress Report September 1-October 10, 2026";

function row(overrides) {
  const { code, reportNumber } = overrides;
  const custom = overrides.periodKind === "custom";
  return {
    reportNumber,
    weekStart: overrides.weekStart,
    ...(custom
      ? {
          periodStart: overrides.periodStart,
          periodEnd: overrides.periodEnd,
          periodKind: "custom",
          periodLabel: overrides.periodLabel || "01 Sep – 10 Oct 2026",
        }
      : {}),
    reportStatus: overrides.reportStatus,
    // Only an Issued report carries the number a person sees.
    publishedNumber: overrides.reportStatus === "Issued" ? overrides.publishedNumber || null : null,
    generationCount: overrides.generationCount || 1,
    dataConfirmedAtUtc: overrides.dataConfirmedAtUtc || "",
    dataConfirmedBy: overrides.dataConfirmedBy || "",
    approvedAtUtc: overrides.approvedAtUtc || "",
    approvedBy: overrides.approvedBy || "",
    issuedAtUtc: overrides.issuedAtUtc || "",
    publishedUrl: overrides.publishedUrl || "",
    pdfUrl: overrides.pdfUrl || "",
    pdfStatus: overrides.pdfStatus || (overrides.reportStatus === "Approved" ? "failed" : "none"),
    etag: `"etag-${code}-${reportNumber}"`,
    llmRunId: "fixture-llm-run",
    docxUrl: `${SITE}/WeeklyReports-${code}/${code}-WR-${reportNumber}.docx`,
    versions: overrides.versions,
    versionsLoaded: true,
  };
}

function line(id, count, items) {
  return { id, count, green: count === 0, ...(items ? { items } : {}) };
}

const HASH_418 = "2".repeat(60) + "bbbb"; // exactly 64 hex chars
const HASH_411S = "4".repeat(60) + "dddd";

export function buildFixtureV2Projects(state = "draft") {
  const red = state === "notReady";
  const lines418 = [
    line("inspection_results", red ? 1 : 0, red ? [{ scheduleRef: "SS418-ITP-014" }] : undefined),
    line(
      "inspection_photos",
      red ? 2 : 0,
      red
        ? [
            { inspectionNumber: 2101, title: "Main engine chock fit", inspectionDate: "2026-10-06", inspector: { upn: "inspector1@example.com" }, category: "Machinery Outfitting", scheduleRef: "", etag: '"fixture-2101"', photoWaiver: null },
            { inspectionNumber: 2108, title: "Shaft alignment, final", inspectionDate: "2026-10-08", inspector: { upn: "inspector2@example.com" }, category: "Machinery Outfitting", scheduleRef: "", etag: '"fixture-2108"', photoWaiver: null },
          ]
        : undefined
    ),
    line("ncr_decisions", 0),
  ];

  const reports418 = [];
  const confirmedAt = "2026-10-10T09:14:00.000Z";
  const base9 = { code: "SS418", reportNumber: 9, weekStart: V2_PERIOD.periodStart, periodKind: "custom", ...V2_PERIOD, dataConfirmedAtUtc: confirmedAt, dataConfirmedBy: V2_ME };
  const v9 = [
    { versionId: "2.0", createdAtUtc: "2026-10-10T10:40:00.000Z", url: versionUrl("SS418", 9, 2) },
    { versionId: "1.0", createdAtUtc: "2026-10-10T09:26:00.000Z", url: versionUrl("SS418", 9, 1) },
  ];
  if (state === "draft") {
    reports418.push(row({ ...base9, reportStatus: "Draft", generationCount: 1, versions: v9 }));
  } else if (state === "approved") {
    reports418.push(row({ ...base9, reportStatus: "Approved", approvedAtUtc: "2026-10-10T11:02:00.000Z", approvedBy: V2_ME, pdfUrl: pdfUrl("SS418", 9, V2_PDF_NAME), pdfStatus: "ready", versions: v9 }));
  } else if (state === "pdfFailed") {
    reports418.push(row({ ...base9, reportStatus: "Approved", approvedAtUtc: "2026-10-10T11:02:00.000Z", approvedBy: V2_ME, pdfUrl: "", pdfStatus: "failed", versions: v9 }));
  } else if (state === "published") {
    reports418.push(row({
      ...base9, reportStatus: "Issued", publishedNumber: 1, approvedAtUtc: "2026-10-10T11:02:00.000Z", approvedBy: V2_ME, issuedAtUtc: "2026-10-10T11:20:00.000Z",
      pdfUrl: pdfUrl("SS418", 9, V2_PDF_NAME), pdfStatus: "ready",
      publishedUrl: `${SITE.replace("ConstructionSupervision", "ss418")}/Weekly%20Reports/${encodeURIComponent(V2_PDF_NAME)}.pdf`, versions: v9,
    }));
  }
  reports418.push(
    row({ code: "SS418", reportNumber: 8, weekStart: V2_CURRENT_WEEK, reportStatus: "Draft", dataConfirmedAtUtc: "2026-10-10T09:00:00.000Z", dataConfirmedBy: V2_ME, versions: [{ versionId: "1.0", createdAtUtc: "2026-10-10T09:14:00.000Z", url: versionUrl("SS418", 8, 1) }] }),
    row({ code: "SS418", reportNumber: 7, weekStart: "2026-09-14", reportStatus: "Draft", dataConfirmedAtUtc: "2026-10-09T14:20:00.000Z", dataConfirmedBy: V2_ME, versions: [{ versionId: "1.0", createdAtUtc: "2026-10-09T14:35:00.000Z", url: versionUrl("SS418", 7, 1) }] })
  );

  const project418 = {
    code: "SS418",
    readiness: { weekStart: V2_CURRENT_WEEK, lines: lines418, readinessHash: HASH_418, allGreen: !red },
    reports: reports418,
  };
  if (state === "generating") {
    project418.generationInProgress = {
      jobId: "9d1b6c9e-0000-4000-8000-000000000009",
      startedAt: "2026-10-10T09:24:00.000Z",
      by: V2_ME,
      state: "running",
      weekStart: V2_PERIOD.periodStart,
      ...V2_PERIOD,
      periodKind: "custom",
      reportNumber: 9,
    };
  }

  const project411s = {
    code: "SS411S",
    readiness: {
      weekStart: V2_CURRENT_WEEK,
      lines: [line("inspection_results", 1, [{ scheduleRef: "SS411S-ITP-031" }]), line("inspection_photos", 0), line("ncr_decisions", 0)],
      readinessHash: HASH_411S,
      allGreen: false,
    },
    reports: [
      row({
        code: "SS411S", reportNumber: 2, weekStart: V2_CURRENT_WEEK, reportStatus: "Draft", generationCount: 2, dataConfirmedAtUtc: "2026-10-09T14:00:00.000Z", dataConfirmedBy: V2_ME,
        versions: [
          { versionId: "2.0", createdAtUtc: "2026-10-09T14:27:00.000Z", url: versionUrl("SS411S", 2, 2) },
          { versionId: "1.0", createdAtUtc: "2026-10-09T14:26:00.000Z", url: versionUrl("SS411S", 2, 1) },
        ],
      }),
      row({
        code: "SS411S", reportNumber: 1, weekStart: "2026-09-28", reportStatus: "Draft", generationCount: 2, dataConfirmedAtUtc: "2026-10-09T08:20:00.000Z", dataConfirmedBy: V2_ME,
        versions: [
          { versionId: "2.0", createdAtUtc: "2026-10-09T08:37:00.000Z", url: versionUrl("SS411S", 1, 2) },
          { versionId: "1.0", createdAtUtc: "2026-10-09T08:36:00.000Z", url: versionUrl("SS411S", 1, 1) },
        ],
      }),
    ],
  };
  return { SS418: project418, SS411S: project411s };
}

/** The period view of every project, as `GET /api/cockpit/reports?periodStart&periodEnd` returns it. */
export function buildFixtureV2Envelope(state = "draft") {
  const byCode = buildFixtureV2Projects(state);
  return {
    generatedAtUtc: "2026-10-10T12:00:00.000Z",
    weekStart: V2_PERIOD.periodStart,
    periodStart: V2_PERIOD.periodStart,
    periodEnd: V2_PERIOD.periodEnd,
    periodKind: "custom",
    errors: [],
    projects: Object.values(byCode).map((p) => ({
      ...p,
      readiness: { ...p.readiness, weekStart: V2_PERIOD.periodStart, ...V2_PERIOD, periodKind: "custom", coveredBy: [] },
    })),
  };
}
